import { Sneaker } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';

export default function TermsOfServicePage() {
  return (
    <div className="min-h-screen bg-white" data-testid="terms-of-service-page">
      <div className="max-w-[800px] mx-auto px-6 md:px-12 py-12">
        <Link to="/" className="flex items-center gap-2 mb-8">
          <Sneaker size={24} weight="duotone" className="text-[#FF3B30]" />
          <span className="font-heading font-black text-lg tracking-tighter uppercase">SOLEFINDER</span>
        </Link>
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-bold uppercase tracking-wide text-[#52525B] hover:text-[#FF3B30] transition-colors mb-8" data-testid="terms-back-btn">
          <span>&larr;</span> Back to Home
        </Link>

        <h1 className="font-heading font-black text-3xl tracking-tighter uppercase mb-2" data-testid="terms-title">Terms of Service</h1>
        <p className="text-sm text-[#A1A1AA] mb-10">Last updated: April 2026</p>

        <div className="prose prose-sm max-w-none text-[#52525B] space-y-8">
          <section>
            <h2 className="font-heading font-bold text-lg text-[#0A0A0A] uppercase tracking-tight">1. Acceptance of Terms</h2>
            <p>By accessing or using SoleFinder (solefinder.app), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, you must not use the Service. We reserve the right to modify these Terms at any time. Your continued use of the Service following any changes constitutes acceptance of the updated Terms.</p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-lg text-[#0A0A0A] uppercase tracking-tight">2. Description of Service</h2>
            <p>SoleFinder is an AI-powered shoe discovery and comparison platform that:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Provides personalised shoe recommendations based on user-submitted fit preferences</li>
              <li>Aggregates and displays shoe pricing information from third-party retailers</li>
              <li>Offers price comparison across multiple stores in Australia, the United Kingdom, and the United States</li>
              <li>Provides outbound links to third-party retailer websites where purchases can be made</li>
            </ul>
            <p className="mt-2"><strong>SoleFinder does not sell shoes directly.</strong> All purchases are made through third-party retailers. We are not a party to any transaction between you and a retailer.</p>
          </section>

          <section id="affiliate">
            <h2 className="font-heading font-bold text-lg text-[#0A0A0A] uppercase tracking-tight">3. Affiliate Disclosure</h2>
            <p>SoleFinder participates in affiliate marketing programmes. This means:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>When you click a link to a retailer on our Service and make a purchase, we may earn a commission from the retailer or affiliate network.</li>
              <li>This commission comes from the retailer at no additional cost to you.</li>
              <li>Affiliate relationships do not influence our shoe recommendations, which are generated by our AI algorithm based on your fit profile.</li>
              <li>We are a participant in affiliate programmes including but not limited to: Amazon Associates, CJ Affiliate, Rakuten Advertising, Awin, Impact, Commission Factory, Sovrn Commerce, ShareASale, and Partnerize.</li>
            </ul>
            <p className="mt-2">In accordance with the US Federal Trade Commission (FTC) guidelines, Australian Competition and Consumer Commission (ACCC) requirements, and UK Advertising Standards Authority (ASA) rules, we disclose that we earn commission from purchases made through links on this Service.</p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-lg text-[#0A0A0A] uppercase tracking-tight">4. User Accounts</h2>
            <p>To access certain features (wishlist, price alerts, quiz history), you may need to create an account. You agree to:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Provide accurate, current, and complete registration information</li>
              <li>Maintain the security of your password and account</li>
              <li>Accept responsibility for all activities under your account</li>
              <li>Notify us immediately of any unauthorised access</li>
            </ul>
            <p className="mt-2">We reserve the right to suspend or terminate accounts that violate these Terms or engage in fraudulent activity.</p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-lg text-[#0A0A0A] uppercase tracking-tight">5. Acceptable Use</h2>
            <p>You agree not to:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Use the Service for any unlawful purpose</li>
              <li>Attempt to gain unauthorised access to the Service, servers, or databases</li>
              <li>Scrape, crawl, or use automated tools to extract data from the Service without our written consent</li>
              <li>Interfere with or disrupt the Service or its infrastructure</li>
              <li>Impersonate any person or entity</li>
              <li>Submit false information, including fake product reports</li>
              <li>Use the Service to send spam or unsolicited communications</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading font-bold text-lg text-[#0A0A0A] uppercase tracking-tight">6. Product Information & Pricing</h2>
            <p>While we strive for accuracy, we cannot guarantee that all product information, images, descriptions, or prices displayed on the Service are accurate, complete, or current. Product data is sourced from third-party retailers and affiliate networks, and may be subject to:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Price changes by retailers without notice to us</li>
              <li>Stock availability changes</li>
              <li>Differences between displayed and actual product images</li>
              <li>Regional pricing variations</li>
              <li>Currency conversion approximations</li>
            </ul>
            <p className="mt-2">Always verify pricing and availability on the retailer's website before completing a purchase. SoleFinder is not responsible for pricing errors or discrepancies.</p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-lg text-[#0A0A0A] uppercase tracking-tight">7. Third-Party Links & Retailers</h2>
            <p>The Service contains links to third-party retailer websites. These links are provided for your convenience. We do not control, endorse, or assume responsibility for:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>The content, products, or services offered by third-party retailers</li>
              <li>The privacy practices of third-party websites</li>
              <li>Any transactions between you and third-party retailers</li>
              <li>Product quality, delivery, returns, or customer service from retailers</li>
            </ul>
            <p className="mt-2">Any disputes regarding purchases must be resolved directly with the retailer.</p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-lg text-[#0A0A0A] uppercase tracking-tight">8. AI Recommendations Disclaimer</h2>
            <p>Our AI-powered shoe recommendations are generated based on the information you provide (foot size, width, activity, preferences) and general shoe data. These recommendations are:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Provided for informational purposes only</li>
              <li>Not a substitute for professional podiatric or medical advice</li>
              <li>Based on general fit data and may not account for individual foot conditions</li>
              <li>Subject to the accuracy and completeness of information you provide</li>
            </ul>
            <p className="mt-2">If you have specific foot health concerns, consult a qualified podiatrist or healthcare professional.</p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-lg text-[#0A0A0A] uppercase tracking-tight">9. Intellectual Property</h2>
            <p>The Service and its original content (excluding user-generated content and third-party product data) are owned by SoleFinder and protected by copyright, trademark, and other intellectual property laws. You may not reproduce, distribute, modify, or create derivative works from any content on the Service without our prior written consent.</p>
            <p className="mt-2">Product names, logos, and brands are property of their respective owners. Their use on this Service is for identification purposes only and does not imply endorsement.</p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-lg text-[#0A0A0A] uppercase tracking-tight">10. Limitation of Liability</h2>
            <p>To the maximum extent permitted by applicable law, SoleFinder and its operators shall not be liable for:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Any indirect, incidental, special, consequential, or punitive damages</li>
              <li>Loss of profits, data, or goodwill</li>
              <li>Any damages resulting from purchases made through third-party retailers</li>
              <li>Inaccurate product information, pricing, or availability data</li>
              <li>Service interruptions, errors, or security breaches</li>
            </ul>
            <p className="mt-2">The Service is provided "as is" and "as available" without warranties of any kind, either express or implied.</p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-lg text-[#0A0A0A] uppercase tracking-tight">11. Indemnification</h2>
            <p>You agree to indemnify, defend, and hold harmless SoleFinder and its operators from any claims, damages, losses, or expenses (including legal fees) arising from your use of the Service, violation of these Terms, or infringement of any third-party rights.</p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-lg text-[#0A0A0A] uppercase tracking-tight">12. Governing Law</h2>
            <p>These Terms are governed by and construed in accordance with the laws of Australia. For users in the United Kingdom, nothing in these Terms affects your statutory rights under UK consumer law. For users in the United States, any disputes shall be resolved in accordance with the laws of the state in which SoleFinder operates.</p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-lg text-[#0A0A0A] uppercase tracking-tight">13. Email Communications</h2>
            <p>By creating an account or subscribing to our email list, you consent to receiving:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Transactional emails (account verification, password reset)</li>
              <li>Price drop alerts for wishlisted products (if enabled)</li>
              <li>New product notifications (if enabled)</li>
              <li>Weekly digest emails (if enabled)</li>
              <li>Marketing communications (if opted in)</li>
            </ul>
            <p className="mt-2">You can manage your email preferences in your account settings or unsubscribe using the link in any email. We comply with CAN-SPAM (US), GDPR (UK/EU), and the Australian Spam Act 2003.</p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-lg text-[#0A0A0A] uppercase tracking-tight">14. Termination</h2>
            <p>We may terminate or suspend your account and access to the Service immediately, without prior notice, for any reason, including breach of these Terms. Upon termination, your right to use the Service ceases immediately. Provisions that by their nature should survive termination shall survive (including Limitation of Liability and Indemnification).</p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-lg text-[#0A0A0A] uppercase tracking-tight">15. Contact Us</h2>
            <p>If you have questions about these Terms of Service, please contact us at:</p>
            <p className="mt-2">
              <strong>SoleFinder</strong><br />
              Email: hello@solefinder.app<br />
              Website: solefinder.app
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
